import axios from "axios";

import { apiConfig } from "@/api/url";
import { getEncryptionKey, getLoginRequest, getMsalInstance } from "@/auth/bootstrapMsal";

import { encrypt } from "./crypto";

export const http = axios.create({
  baseURL: apiConfig.baseUrl || undefined,
  headers: { Accept: "application/json" },
});

const MAX_CONCURRENT = 2;
const REQUEST_DELAY = 300;

// Encryption is enabled only when a non-empty key exists
const ENCRYPTION_ENABLED = () => {
  try {
    return Boolean(getEncryptionKey()?.trim());
  } catch {
    return false;
  }
};
let activeRequests = 0;
let lastRequestTime = 0;

const requestQueue: Array<{
  resolve: (v: any) => void;
  config: any;
}> = [];

async function processQueue() {
  if (requestQueue.length === 0) return;

  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;

  if (activeRequests < MAX_CONCURRENT && timeSinceLastRequest >= REQUEST_DELAY) {
    activeRequests++;
    lastRequestTime = Date.now();

    const { resolve, config } = requestQueue.shift()!;
    resolve(config);
  } else {
    setTimeout(processQueue, Math.max(REQUEST_DELAY - timeSinceLastRequest, 50));
  }
}

http.interceptors.request.use(async (config) => {
  config.headers = config.headers ?? {};

  const cid = globalThis.crypto?.randomUUID?.() ?? String(Date.now());

  config.headers["X-Correlation-ID"] = cid;

  const token = await getAccessTokenSilentlySafe();

  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  const method = config.method?.toLowerCase();

  const isMultipart = config.headers["Content-Type"]?.includes("multipart/form-data");

  const isAuthRequest = config.url?.includes("/auth-config");

  if (isMultipart || isAuthRequest) {
    return config;
  }

  // console.log("METHOD:", method);

  // =============================
  // QUERY PARAM ENCRYPTION (GET)
  // =============================
  if (ENCRYPTION_ENABLED() && method === "get" && config.params) {
    config.params = {
      e: await encrypt(config.params),
    };

    // console.log("🔐 Encrypted QUERY:", config.params);
  }

  // =============================
  // BODY ENCRYPTION
  // =============================
if (
  ENCRYPTION_ENABLED() &&
  config.data &&
  ["post", "put", "patch", "delete"].includes(method!)
) {    // console.log("DATA BEFORE ENCRYPT:", config.data);

    config.data = {
      e: await encrypt(config.data),
    };

    // console.log("🔐 Encrypted BODY:", config.data);
  }

  return config;
});

http.interceptors.response.use(
  (response) => {
    activeRequests--;
    processQueue();

    return response;
  },
  (err) => {
    activeRequests--;
    processQueue();

    if (err.code === "ERR_CANCELED" || err.name === "CanceledError" || err.name === "AbortError") {
      return Promise.reject(err);
    }

    const resp = err.response;

    if (resp?.data?.error) {
      return Promise.reject({
        status: resp.status,
        code: resp.data.error.code,
        message: resp.data.error.message,
        details: resp.data.error.details,
        traceId: resp.data.traceId,
      });
    }

    return Promise.reject({
      status: resp?.status ?? 0,
      code: "network_error",
      message: err.message ?? "Network error",
    });
  }
);

async function getAccessTokenSilentlySafe(): Promise<string | null> {
  try {
    const msal = getMsalInstance();
    const account = msal.getActiveAccount();

    if (!account) {
      return null;
    }

    const scopes = getLoginRequest().scopes;

    const result = await msal.acquireTokenSilent({
      account,
      scopes,
    });

    return result.accessToken ?? null;
  } catch {
    return null;
  }
}
